import { Button, Typography } from "@mui/material";
import { OpenFolder } from "features/Runs/helpers";
import { RunsFolderTreeActions } from "features/Runs/state/folder-tree/actions";
import {
  useRunFolderQueryProvider,
  useRunsFolderTreeState,
  useRunsModalState,
} from "features/Runs/state/hooks";
import {
  DirectoryFolder,
  NodeType,
  useCancelRunMutation,
} from "graphql/generated/schema-types";
import { useWindowSize } from "hooks/useWindowSize";
import { StyledCard } from "shared-components/styled-components";
import { Alert, AlertType } from "shared-components/toast";
import styled from "styled-components";
import { ModalProps } from "./ModalProps";

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: right;
  align-items: center;
  margin: 0 10px;
  gap: 10px;
`;

export const CancelRunDialog = ({ handleClose }: ModalProps) => {
  const foldersQuery = useRunFolderQueryProvider();
  const {
    runsModalState: { selectedRunId },
  } = useRunsModalState();
  const { runsFolderTreeDispatch } = useRunsFolderTreeState();
  const [cancelRun] = useCancelRunMutation();
  const windowSize = useWindowSize();

  const handleConfirm = async () => {
    if (!selectedRunId) return;

    try {
      const response = await cancelRun({
        variables: { runId: selectedRunId },
      });

      const folderQueryResponse = await foldersQuery.refetch();

      runsFolderTreeDispatch({
        type: RunsFolderTreeActions.NODE_SELECTED,
        payload: {
          currentNode: {
            id: response.data?.cancelRun?.folderId ?? 0,
            type: NodeType.Folder,
          },
        },
      });

      OpenFolder(
        (folderQueryResponse.data.directory as DirectoryFolder[]) ?? [],
        {
          id: response.data?.cancelRun?.folderParentId ?? 0,
          type: NodeType.Folder,
        }
      );

      Alert({ type: AlertType.SUCCESS, message: "Cancelled Run" });
    } catch (err) {
      Alert({ type: AlertType.ERROR, message: "Failed to cancel run" });
      console.error(err);
    } finally {
      handleClose();
    }
  };

  const dialogWidth =
    windowSize.width! > 2000
      ? "20vw"
      : windowSize.width! > 1500
      ? "35vw"
      : "50vw";

  return (
    <StyledCard width={dialogWidth}>
      <Typography>
        Cancelling this run will cancel the remaining tasks and move the run to
        the cancelled runs folder. Would you like to continue?
      </Typography>
      <ButtonContainer>
        <Button onClick={handleClose} variant="contained" color="secondary">
          No
        </Button>
        <Button onClick={handleConfirm} variant="contained" color="primary">
          Yes
        </Button>
      </ButtonContainer>
    </StyledCard>
  );
};
